import React from 'react';
import {
  TitleBar,
  Layout,
  StatusMessage,
  Button,
  Card,
  TextField,
  ButtonGroup
} from '../components/library-components';
import { getMeteringSettings, getNewMeteringSettings, getMeteringDomains, createOrUpdateMeteringSetting } from '../api';
import MeteringRule from '../components/metering_rule/metering_rule';
import _ from 'lodash-es';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { Dialog } from '@quintype/em/components/dialog';
import { Button as EmButton } from '@quintype/em/components/button';
import './paywall_settings.scss';

const ANONYMOUS_CONFIGURATION_TYPES = [
  {
    user_type: 'anonymous',
    asset_access_type: 'login'
  },
  {
    user_type: 'anonymous',
    asset_access_type: 'subscription'
  }
];

const LOGGED_IN_CONFIGURATION_TYPES = [
  {
    user_type: 'logged_in',
    asset_access_type: 'subscription'
  }
];

const meterDefaultValues = {
  asset_limit: 5,
  asset_type: 'story',
  duration_value: 2,
  duration_unit: 'days'
};

const DEFAULT_DOMAIN = '__default__';
const NEW_DOMAIN_TAB = '__new_domain__';

class PaywallSettings extends React.Component {
  constructor (props) {
    super(props);
    this.state = {
      showNotification: {
        isNotificationOpen: false,
        status: '',
        statusMessage: ''
      },
      meteringSettings: [],
      domains: [DEFAULT_DOMAIN],
      selectedDomainIndex: 0,
      newDomainName: '',
      domainError: '',
      isNewDomainMode: false,
      currentDomainName: '',
      isSaving: false,
      hasChanges: false,
      isUnsavedChangesModalOpen: false,
      pendingTabIndex: null,
      initialMeteringSettings: null
    };
  }

  scrollToTop () {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  componentDidMount () {
    this.loadDomainsAndSettings();
  }

  loadDomainsAndSettings () {
    // Load initial metering settings template once and cache it
    Promise.all([
      getMeteringDomains(),
      getNewMeteringSettings()
    ]).then(([{ domains }, initialMeteringSettings]) => {
      // Cache the initial settings template
      this.setState({ initialMeteringSettings });

      const allDomains = [DEFAULT_DOMAIN, ...domains];

      this.setState({ domains: allDomains }, () => {
        this.loadSettingsForDomain(this.getCurrentDomain());
      });
    }).catch(() => {
      // On error, show default tab
      this.setState({ domains: [DEFAULT_DOMAIN] }, () => {
        this.loadSettingsForDomain(null);
      });
    });
  }

  getCurrentDomain () {
    const { domains, selectedDomainIndex, isNewDomainMode } = this.state;
    if (isNewDomainMode) {
      return null;
    }
    const domain = domains[selectedDomainIndex];
    return domain === DEFAULT_DOMAIN ? null : domain;
  }

  buildMeteringSettings (domain, existingSettings = []) {
    const { initialMeteringSettings } = this.state;
    const allConfigTypes = [...ANONYMOUS_CONFIGURATION_TYPES, ...LOGGED_IN_CONFIGURATION_TYPES];

    return allConfigTypes.map((configType) => {
      return (
        _.find(existingSettings, configType) ||
        Object.assign(
          {},
          initialMeteringSettings,
          {
            wall_type: configType.asset_access_type === 'login' ? 'registration_wall' : 'pay_wall',
            domain: domain
          },
          configType,
          meterDefaultValues
        )
      );
    });
  }

  loadSettingsForDomain (domain) {
    const domainParam = domain === null ? '' : domain;

    getMeteringSettings(domainParam).then((meteringSettings) => {
      this.setState({
        meteringSettings: this.buildMeteringSettings(domain, meteringSettings),
        currentDomainName: domain || '',
        hasChanges: false
      });
    });
  }

  loadNewDomainSettings () {
    this.setState({
      meteringSettings: this.buildMeteringSettings(null),
      hasChanges: false
    });
  }

  handleDomainTabChange (index) {
    const { domains, isNewDomainMode, hasChanges } = this.state;

    // If clicking on the "New Domain" tab
    if (isNewDomainMode && index === domains.length) {
      return; // Already on new domain tab
    }

    // If there are unsaved changes, show confirmation modal
    if (hasChanges) {
      this.setState({
        isUnsavedChangesModalOpen: true,
        pendingTabIndex: index
      });
      return;
    }

    this.proceedWithTabChange(index);
  }

  proceedWithTabChange (index) {
    const { isNewDomainMode } = this.state;

    const newState = {
      selectedDomainIndex: index,
      domainError: '',
      hasChanges: false,
      isUnsavedChangesModalOpen: false,
      pendingTabIndex: null,
      ...(isNewDomainMode && { isNewDomainMode: false, newDomainName: '' })
    };

    this.setState(newState, () => {
      this.loadSettingsForDomain(this.getCurrentDomain());
    });
  }

  handleDiscardChanges () {
    const { pendingTabIndex } = this.state;
    if (pendingTabIndex !== null) {
      this.proceedWithTabChange(pendingTabIndex);
    } else {
      this.setState({
        isUnsavedChangesModalOpen: false,
        pendingTabIndex: null
      });
    }
  }

  handleGoBackAndSave () {
    this.setState({
      isUnsavedChangesModalOpen: false,
      pendingTabIndex: null
    });
  }

  updateMeteringSettings (updatedMeteringSetting) {
    const userType = updatedMeteringSetting.user_type;

    const settingsWithoutChanges = this.state.meteringSettings.filter(
      (obj) =>
        !(obj.user_type === userType &&
        obj.asset_access_type === updatedMeteringSetting.asset_access_type)
    );

    const newState = [...settingsWithoutChanges, updatedMeteringSetting];

    this.setState({ meteringSettings: newState, hasChanges: true });
  }

  showStatusMessage (statusMessage) {
    this.setState({
      showNotification: {
        isNotificationOpen: true,
        status: statusMessage.status,
        statusMessage: statusMessage.statusMessage
      }
    });
  }

  handleNewDomainClick () {
    // If there are unsaved changes, show confirmation modal
    if (this.state.hasChanges) {
      this.setState({
        isUnsavedChangesModalOpen: true,
        pendingTabIndex: 'new_domain'
      });
      return;
    }

    this.proceedWithNewDomain();
  }

  proceedWithNewDomain () {
    this.setState({
      isNewDomainMode: true,
      selectedDomainIndex: this.state.domains.length,
      newDomainName: '',
      domainError: '',
      hasChanges: false,
      isUnsavedChangesModalOpen: false,
      pendingTabIndex: null
    }, () => {
      this.loadNewDomainSettings();
    });
  }

  normalizeDomain (domain) {
    if (!domain) return '';

    let normalized = domain.trim().toLowerCase();
    // Remove protocol (http://, https://, ftp://, etc.)
    normalized = normalized.replace(/^[a-z]+:\/\//i, '');
    // Remove www. prefix
    normalized = normalized.replace(/^www\./i, '');
    // Remove trailing slashes and paths
    normalized = normalized.replace(/\/.*$/, '');

    return normalized;
  }

  validateDomain (domain, excludeIndex = null) {
    const normalizedDomain = this.normalizeDomain(domain);
    const domainRegex = /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/i;

    if (!normalizedDomain || normalizedDomain.trim() === '') {
      return 'Domain name is required';
    }
    if (!domainRegex.test(normalizedDomain)) {
      return 'Please enter a valid domain name';
    }
    // Check for duplicates, excluding the specified index (for edit mode)
    const domainsToCheck = excludeIndex !== null
      ? this.state.domains.filter((d, i) => i !== excludeIndex && d !== DEFAULT_DOMAIN)
      : this.state.domains;
    if (domainsToCheck.includes(normalizedDomain)) {
      return 'This domain already exists';
    }
    return '';
  }

  handleDomainChange (value, isNewDomain = false) {
    const normalizedDomain = this.normalizeDomain(value);
    const stateKey = isNewDomain ? 'newDomainName' : 'currentDomainName';

    // Update domain in all metering settings
    const updatedSettings = this.state.meteringSettings.map(setting => ({
      ...setting,
      domain: normalizedDomain || null
    }));

    this.setState({
      [stateKey]: normalizedDomain,
      domainError: '',
      hasChanges: true,
      meteringSettings: updatedSettings
    });
  }

  handleDomainSaved (savedDomain) {
    // When a metering setting is saved successfully with the new domain
    if (savedDomain && !this.state.domains.includes(savedDomain)) {
      const newDomains = [...this.state.domains, savedDomain];
      const newIndex = newDomains.length - 1;
      this.setState({
        domains: newDomains,
        selectedDomainIndex: newIndex,
        isNewDomainMode: false,
        newDomainName: '',
        currentDomainName: savedDomain,
        domainError: '',
        hasChanges: false
      });
    }
  }

  showValidationError (domainError) {
    this.setState({ domainError }, () => this.scrollToTop());
  }

  async saveAllSettings () {
    const { isNewDomainMode, newDomainName, currentDomainName, meteringSettings, domains, selectedDomainIndex } = this.state;
    const isDefaultDomain = domains[selectedDomainIndex] === DEFAULT_DOMAIN;

    // Validate domain
    const domainToValidate = isNewDomainMode ? newDomainName : currentDomainName;
    const excludeIndex = isNewDomainMode ? null : selectedDomainIndex;
    const shouldValidate = isNewDomainMode || !isDefaultDomain;

    if (shouldValidate) {
      const domainError = this.validateDomain(domainToValidate, excludeIndex);
      if (domainError) {
        this.showValidationError(domainError);
        return;
      }
    }

    this.setState({ isSaving: true });

    try {
      // Save all metering settings
      const saveMeteringSettings = meteringSettings.map(setting =>
        createOrUpdateMeteringSetting(setting)
      );

      const results = await Promise.all(saveMeteringSettings);

      // Update state with saved settings
      this.setState({
        meteringSettings: results,
        hasChanges: false,
        isSaving: false
      });

      // If this was a new domain, add it to the list
      if (isNewDomainMode && newDomainName) {
        this.handleDomainSaved(newDomainName);
      }

      // If domain was changed, update the domains list
      if (!isNewDomainMode && !isDefaultDomain && currentDomainName !== domains[selectedDomainIndex]) {
        const updatedDomains = [...domains];
        updatedDomains[selectedDomainIndex] = currentDomainName;
        this.setState({ domains: updatedDomains });
      }

      this.showStatusMessage({
        status: 'success',
        statusMessage: 'All metering settings saved successfully'
      });
    } catch (error) {
      this.setState({ isSaving: false });
      this.showStatusMessage({
        status: 'error',
        statusMessage: `Failed to save settings: ${error.errors ? error.errors[0] : 'Unknown error'}`
      });
    }
  }

  renderDomainDetails (isNewDomain = false) {
    const { currentDomainName, newDomainName, domainError, domains, selectedDomainIndex } = this.state;
    const isDefaultDomain = !isNewDomain && domains[selectedDomainIndex] === DEFAULT_DOMAIN;

    // Default domain shows as read-only text
    if (isDefaultDomain) {
      return (
        <Card>
          <div className="domain-details">
            <h4 className="domain-details__label">Domain Name</h4>
            <div className="domain-details__value">Default (All Whitelisted Domains)</div>
          </div>
        </Card>
      );
    }

    // New domain and non-default domains show editable input
    const value = isNewDomain ? newDomainName : currentDomainName;

    return (
      <Card>
        <div className="domain-details">
          <h4 className="domain-details__label">Domain Name</h4>
          <TextField
            placeholder="Enter domain name"
            value={value}
            onChange={(e) => this.handleDomainChange(e.target.value, isNewDomain)}
            error={domainError}
            dataTest={isNewDomain ? 'new-domain-input' : 'domain-input'}
          />
        </div>
      </Card>
    );
  }

  renderMeteringRules (isNewDomain = false) {
    const currentDomain = isNewDomain ? this.state.newDomainName : this.getCurrentDomain();
    const domainKey = currentDomain || 'default';

    return (
      <>
        <h2 className="metered-settings-user-title">Anonymous Users</h2>
        {this.state.meteringSettings.length > 0 &&
          ANONYMOUS_CONFIGURATION_TYPES.map((config) => (
            <MeteringRule
              key={`${config.user_type}-${config.asset_access_type}-${domainKey}`}
              meteringSetting={_.find(this.state.meteringSettings, config)}
              showStatusMessage={this.showStatusMessage.bind(this)}
              updateMeteringSettings={this.updateMeteringSettings.bind(this)}
            />
          ))}

        <h2 className="metered-settings-user-title">Logged in Users</h2>
        {this.state.meteringSettings.length > 0 &&
          LOGGED_IN_CONFIGURATION_TYPES.map((config) => (
            <MeteringRule
              key={`${config.user_type}-${config.asset_access_type}-${domainKey}`}
              meteringSetting={_.find(this.state.meteringSettings, config)}
              showStatusMessage={this.showStatusMessage.bind(this)}
              updateMeteringSettings={this.updateMeteringSettings.bind(this)}
            />
          ))}
      </>
    );
  }

  renderSaveButton () {
    return (
      <ButtonGroup>
        <Button
          primary
          onClick={() => this.saveAllSettings()}
          disabled={this.state.isSaving}
          dataTest="master-save-button"
        >
          {this.state.isSaving ? 'Saving...' : 'Save'}
        </Button>
      </ButtonGroup>
    );
  }

  renderTabPanelContent (isNewDomain = false) {
    return (
      <div className="domain-content">
        <div className="domain-header">
          <h3 className="domain-header__title">Domain Details</h3>
        </div>
        {this.renderDomainDetails(isNewDomain)}
        {this.renderMeteringRules(isNewDomain)}
        {this.renderSaveButton()}
      </div>
    );
  }

  renderUnsavedChangesModal () {
    return (
      <Dialog
        isOpen={this.state.isUnsavedChangesModalOpen}
        onClose={() => this.handleGoBackAndSave()}
      >
        <div className="unsaved-changes-modal">
          <h3 className="unsaved-changes-modal__title">You have unsaved changes</h3>
          <p className="unsaved-changes-modal__description">
            Make sure to save before leaving by clicking on the Save button at the bottom of the page.
          </p>
          <div className="unsaved-changes-modal__actions">
            <EmButton
              type="secondary"
              onClick={() => this.handleDiscardChanges()}
            >
              Discard Changes
            </EmButton>
            <EmButton
              type="primary"
              onClick={() => this.handleGoBackAndSave()}
            >
              Go back and Save
            </EmButton>
          </div>
        </div>
      </Dialog>
    );
  }

  render () {
    const { domains, selectedDomainIndex, isNewDomainMode } = this.state;

    // Build tabs array
    const tabs = [...domains];
    if (isNewDomainMode) {
      tabs.push(NEW_DOMAIN_TAB);
    }

    const activeIndex = isNewDomainMode ? domains.length : selectedDomainIndex;

    return (
      <Layout>
        <div className="metered-settings-header">
          <TitleBar title="Metered Settings" />
          <Button
            primary
            onClick={() => this.handleNewDomainClick()}
            dataTest="new-domain-button"
            disabled={isNewDomainMode}
          >
            New Domain
          </Button>
        </div>

        <div className="domain-tabs-container">
          <Tabs
            selectedIndex={activeIndex}
            onSelect={(index) => this.handleDomainTabChange(index)}
            className="domain-tabs"
          >
            <TabList className="domain-tab-list">
              {tabs.map((domain, index) => (
                <Tab key={domain} className="domain-tab">
                  {domain === DEFAULT_DOMAIN
                    ? 'Default'
                    : domain === NEW_DOMAIN_TAB
                      ? 'New Domain'
                      : domain}
                </Tab>
              ))}
            </TabList>

            {domains.map((domain) => (
              <TabPanel key={domain}>
                {this.renderTabPanelContent(false)}
              </TabPanel>
            ))}

            {isNewDomainMode && (
              <TabPanel key={NEW_DOMAIN_TAB}>
                {this.renderTabPanelContent(true)}
              </TabPanel>
            )}
          </Tabs>
        </div>

        {this.renderUnsavedChangesModal()}

        {this.state.showNotification.isNotificationOpen && (
          <StatusMessage
            statusMessage={this.state.showNotification.statusMessage}
            status={this.state.showNotification.status}
            onCancel={() =>
              this.setState({
                showNotification: {
                  isNotificationOpen: false,
                  status: '',
                  statusMessage: ''
                }
              })
            }
          />
        )}
      </Layout>
    );
  }
}

export default PaywallSettings;
